// src/pages/HousekeepingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Card,
  Button,
  Tag,
  notification,
  Spin,
  Empty,
  Row,
  Col,
  Space,
  Input,
  Select,
  Statistic,
  Tooltip,
  Modal,
  Switch,
  Segmented,
  Badge,
  Typography,
  Divider,
  Checkbox,
  Popconfirm,
} from "antd";
import {
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  HomeOutlined,
  CheckOutlined,
  HighlightOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import housekeepingApi from "../api/housekeepingApi";
import HousekeepingModal from "./HousekeepingModal";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";

const { Search } = Input;
const { Text, Title } = Typography;

const TASK_STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "InProgress",
  COMPLETED: "Completed",
};

const PRIORITY_COLORS = {
  Low: "green",
  Normal: "blue",
  Medium: "blue",
  High: "orange",
  Urgent: "red",
  Emergency: "red",
};

const STATUS_META = {
  [TASK_STATUS.PENDING]: { label: "Pending", color: "orange", icon: <ClockCircleOutlined /> },
  [TASK_STATUS.IN_PROGRESS]: { label: "In Progress", color: "blue", icon: <SyncOutlined spin /> },
  [TASK_STATUS.COMPLETED]: { label: "Completed", color: "green", icon: <CheckCircleOutlined /> },
};

/* ------------------------
   Safe property readers (camelCase/PascalCase)
------------------------ */
const pick = (o, keys) => keys.find((k) => o && Object.prototype.hasOwnProperty.call(o, k)) ?? null;
const read = (o, keys) => (o && keys.some((k) => k in o) ? o[pick(o, keys)] : undefined);

const taskIdOf = (t) => read(t, ["housekeepingTaskId", "HousekeepingTaskId", "id", "Id"]);
const itemIdOf = (it) => read(it, ["checklistItemId", "ChecklistItemId", "id", "Id"]) ?? 0;
const itemDescOf = (it) => read(it, ["description", "Description"]) ?? "";
const itemDoneOf = (it) => !!read(it, ["isCompleted", "IsCompleted"]);
const itemDoneAtOf = (it) => read(it, ["completedAt", "CompletedAt"]);

const normalizeChecklistItem = (it) => ({
  checklistItemId: itemIdOf(it),
  description: itemDescOf(it),
  isCompleted: itemDoneOf(it),
  completedAt: itemDoneAtOf(it) ?? null,
  // keep any temp keys if present
  _tempId: it?._tempId,
});

const HousekeepingPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;
  const userId = authData?.userId;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTask, setSelectedTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [searchValue, setSearchValue] = useState("");

  const [showInfo, setShowInfo] = useState(false);

  // checklist modal
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistTask, setChecklistTask] = useState(null);
  const [newChecklistText, setNewChecklistText] = useState("");

  /* ------------------------
     AntD notifications (v5/v6)
  ------------------------ */
  const notifySuccess = (message, description) => notification.success({ message, description });
  const notifyError = (message, description) => notification.error({ message, description });
  const notifyWarning = (message, description) => notification.warning({ message, description });

  /* ------------------------
     API helpers
  ------------------------ */
  const apiGetMyTasks = async () => housekeepingApi.getMyTasks(hotelId);
  const apiUpdateTask = async (taskId, dto) => housekeepingApi.updateTask(taskId, hotelId, dto);
  const apiGetTaskById = async (taskId) => housekeepingApi.getTaskById(taskId, hotelId);
  const apiGetChecklistItems = async (taskId) => housekeepingApi.getChecklistItems(taskId);
  const apiUpdateChecklist = async (taskId, itemsDtoArray) => housekeepingApi.updateChecklist(taskId, itemsDtoArray);

  /* ------------------------
     Fetch tasks
  ------------------------ */
  const fetchHousekeepingTasks = async () => {
    if (!hotelId) {
      setError("Hotel context missing. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiGetMyTasks();
      const data = response?.data;

      // API sometimes returns { message: "..."} instead of []
      const list = Array.isArray(data) ? data : [];

      const formatted = list.map((task) => ({
        housekeepingTaskId: taskIdOf(task),
        title: read(task, ["title", "Title"]) ?? "",
        description: read(task, ["description", "Description"]) ?? "",
        roomId: read(task, ["roomId", "RoomId"]),
        roomNumber: read(task, ["roomNumber", "RoomNumber"]) || read(task, ["room", "Room"])?.roomNumber || "N/A",
        status: read(task, ["status", "Status"]) || TASK_STATUS.PENDING,
        priority: read(task, ["priority", "Priority"]) || "Normal",
        hotelId: read(task, ["hotelId", "HotelId"]) || hotelId,
        assignedAt: read(task, ["assignedAt", "AssignedAt"]) || read(task, ["createdDate", "CreatedDate"]) || null,
        notes: read(task, ["notes", "Notes", "assignmentNotes", "AssignmentNotes"]) || "",
      }));

      setTasks(formatted);
    } catch (err) {
      console.error("Failed to fetch housekeeping tasks:", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to load housekeeping tasks";
      setError(msg);
      notifyError("Error", msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHousekeepingTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, userId, refreshKey]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setRefreshKey((k) => k + 1), 60000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  /* ------------------------
     Edit modal
  ------------------------ */
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleTaskUpdate = async (updatedTask) => {
    try {
      const updateDto = {
        status: updatedTask.status,
        notes: updatedTask.notes || null,
        completedAt: updatedTask.status === TASK_STATUS.COMPLETED ? new Date().toISOString() : null,
      };

      await apiUpdateTask(updatedTask.housekeepingTaskId, updateDto);

      setTasks((prev) =>
        prev.map((t) => (t.housekeepingTaskId === updatedTask.housekeepingTaskId ? { ...t, ...updatedTask } : t))
      );

      notifySuccess("Success", "Task updated successfully");
      closeEditModal();
    } catch (err) {
      console.error("Update failed:", err?.response?.data || err);
      notifyError("Error", err?.response?.data?.message || "Failed to update task");
    }
  };

  /* ------------------------
     Checklist: load, refresh, save (bulk PUT)
  ------------------------ */
  const openChecklist = async (task) => {
    const taskId = task?.housekeepingTaskId;
    if (!taskId) return;

    setChecklistOpen(true);
    setChecklistLoading(true);

    setChecklistTask({
      ...task,
      housekeepingTaskId: taskId,
      checklistItems: [],
    });
    setNewChecklistText("");

    try {
      const [taskRes, itemsRes] = await Promise.allSettled([apiGetTaskById(taskId), apiGetChecklistItems(taskId)]);

      const rawTask = taskRes.status === "fulfilled" ? taskRes.value?.data : null;

      let items = [];
      if (itemsRes.status === "fulfilled") {
        items = Array.isArray(itemsRes.value?.data) ? itemsRes.value.data : [];
      } else {
        // GET checklist returns 404 if empty
        const code = itemsRes.reason?.response?.status;
        if (code !== 404) throw itemsRes.reason;
      }

      setChecklistTask((prev) => ({
        ...prev,
        ...(rawTask || {}),
        housekeepingTaskId: taskIdOf(rawTask) ?? taskId,
        title: read(rawTask, ["title", "Title"]) ?? prev?.title ?? "",
        roomNumber: read(rawTask, ["roomNumber", "RoomNumber"]) ?? prev?.roomNumber ?? "N/A",
        status: read(rawTask, ["status", "Status"]) ?? prev?.status ?? TASK_STATUS.PENDING,
        notes: read(rawTask, ["notes", "Notes"]) ?? prev?.notes ?? "",
        checklistItems: items.map(normalizeChecklistItem),
      }));
    } catch (e) {
      console.error("Failed to load checklist:", e);
      notifyError("Failed to load checklist", e?.response?.data?.message || "Please try again.");
    } finally {
      setChecklistLoading(false);
    }
  };

  const refreshChecklist = async () => {
    const taskId = checklistTask?.housekeepingTaskId;
    if (!taskId) return;

    setChecklistLoading(true);
    try {
      const [taskRes, itemsRes] = await Promise.allSettled([apiGetTaskById(taskId), apiGetChecklistItems(taskId)]);

      const rawTask = taskRes.status === "fulfilled" ? taskRes.value?.data : null;

      let items = [];
      if (itemsRes.status === "fulfilled") {
        items = Array.isArray(itemsRes.value?.data) ? itemsRes.value.data : [];
      } else {
        const code = itemsRes.reason?.response?.status;
        if (code !== 404) throw itemsRes.reason;
      }

      setChecklistTask((prev) => ({
        ...prev,
        ...(rawTask || {}),
        status: read(rawTask, ["status", "Status"]) ?? prev?.status,
        notes: read(rawTask, ["notes", "Notes"]) ?? prev?.notes,
        checklistItems: items.map(normalizeChecklistItem),
      }));
    } catch (e) {
      notifyError("Failed to refresh checklist", e?.response?.data?.message || "Please try again.");
    } finally {
      setChecklistLoading(false);
    }
  };

  const saveChecklistItems = async (nextItems) => {
    const taskId = checklistTask?.housekeepingTaskId;
    if (!taskId) return;

    // DTO expected by backend: List<ChecklistItemDto>
    // NOTE: do NOT send completedAt. Server should control CompletedAt.
    const dto = (nextItems || [])
      .map((x) => ({
        checklistItemId: itemIdOf(x), // 0 for new items
        description: itemDescOf(x),
        isCompleted: itemDoneOf(x),
      }))
      .filter((x) => (x.description || "").trim().length > 0);

    setChecklistLoading(true);
    try {
      const res = await apiUpdateChecklist(taskId, dto);

      // If your updated backend returns the updated checklist + status, use it immediately
      const payload = res?.data;
      const returnedChecklist = Array.isArray(payload?.checklist) ? payload.checklist : null;

      if (returnedChecklist) {
        setChecklistTask((prev) => ({
          ...prev,
          status: payload?.taskStatus ?? prev?.status,
          checklistItems: returnedChecklist.map(normalizeChecklistItem),
        }));
      } else {
        // Fallback if backend returns only { message: "..." }
        await refreshChecklist();
      }

      await fetchHousekeepingTasks();
    } catch (e) {
      console.error("Failed to save checklist:", e);
      notifyError("Failed to update checklist", e?.response?.data?.message || "Please try again.");
      await refreshChecklist();
    } finally {
      setChecklistLoading(false);
    }
  };

  const toggleChecklistItem = async (item, checked) => {
    const id = itemIdOf(item);

    // Optimistic UI: show a timestamp immediately even before server response
    const next = (checklistTask?.checklistItems || []).map((x) => {
      const same =
        itemIdOf(x) === id ||
        (id === 0 && x._tempId && item._tempId && x._tempId === item._tempId);

      if (!same) return x;

      return {
        ...x,
        isCompleted: checked,
        completedAt: checked ? new Date().toISOString() : null,
      };
    });

    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
  };

  const addChecklistItem = async () => {
    const text = newChecklistText.trim();
    if (!text) return;

    const tempId = `tmp-${Date.now()}-${Math.random()}`;
    const next = [
      ...(checklistTask?.checklistItems || []),
      { checklistItemId: 0, description: text, isCompleted: false, completedAt: null, _tempId: tempId },
    ];

    setNewChecklistText("");
    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
    notifySuccess("Checklist updated", "Checklist item added");
  };

  const deleteChecklistItem = async (item) => {
    const id = itemIdOf(item);

    const next = (checklistTask?.checklistItems || []).filter((x) => {
      // for unsaved temp items, match by _tempId
      if (id === 0) return x._tempId !== item._tempId;
      return itemIdOf(x) !== id;
    });

    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
    notifySuccess("Checklist updated", "Checklist item deleted");
  };

  const checklistProgress = useMemo(() => {
    const items = checklistTask?.checklistItems || [];
    const total = items.length;
    const done = items.filter((x) => itemDoneOf(x)).length;
    return { total, done, allDone: total > 0 && done === total };
  }, [checklistTask]);

  const markTaskCompletedFromChecklist = async () => {
    if (!checklistProgress.allDone) {
      notifyWarning("Checklist not complete", "Complete all checklist items before marking the task as Completed.");
      return;
    }

    // Backend auto-completes task when all checklist items are done
    await refreshChecklist();
    await fetchHousekeepingTasks();
    setChecklistOpen(false);
    notifySuccess("Success", "Task completed (auto-completed by checklist).");
  };

  /* ------------------------
     Filtering + counts
  ------------------------ */
  const filteredTasks = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return tasks.filter((t) => {
      const statusOk = statusFilter === "All" || t.status === statusFilter;
      const priorityOk = priorityFilter === "All" || t.priority === priorityFilter;
      const text = `${t.title} ${t.description} ${t.roomNumber}`.toLowerCase();
      const searchOk = q === "" || text.includes(q);
      return statusOk && priorityOk && searchOk;
    });
  }, [tasks, statusFilter, priorityFilter, searchValue]);

  const counts = useMemo(() => {
    const c = { pending: 0, inProgress: 0, completed: 0 };
    for (const t of tasks) {
      if (t.status === TASK_STATUS.PENDING) c.pending++;
      else if (t.status === TASK_STATUS.IN_PROGRESS) c.inProgress++;
      else if (t.status === TASK_STATUS.COMPLETED) c.completed++;
    }
    return c;
  }, [tasks]);

  const renderStatus = (status) => {
    const meta = STATUS_META[status] || { label: "Unknown", color: "default", icon: null };
    return (
      <Tag color={meta.color} icon={meta.icon}>
        {meta.label}
      </Tag>
    );
  };

  /* ------------------------
     Main table columns
  ------------------------ */
  const columns = [
    {
      title: "Task",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{text}</Text>
          <Space size="small">
            <Tag icon={<HomeOutlined />} color="default">
              Room {record.roomNumber || "N/A"}
            </Tag>
            <Tag color={PRIORITY_COLORS[record.priority] || "default"}>{record.priority}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      width: 160,
      filters: Object.values(TASK_STATUS).map((s) => ({ text: STATUS_META[s]?.label || s, value: s })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Assigned On",
      dataIndex: "assignedAt",
      key: "assignedAt",
      render: (date) => (date ? new Date(date).toLocaleString() : "N/A"),
      sorter: (a, b) => new Date(a.assignedAt || 0) - new Date(b.assignedAt || 0),
      width: 200,
    },
    {
      title: "Actions",
      key: "actions",
      width: 240,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit task">
            <Button icon={<EditOutlined />} onClick={() => handleEditTask(record)} />
          </Tooltip>

          <Tooltip title="Checklist">
            <Button icon={<UnorderedListOutlined />} onClick={() => openChecklist(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /* ------------------------
     Checklist table (modal)
  ------------------------ */
  const checklistColumns = [
    {
      title: "Done",
      key: "done",
      width: 80,
      render: (_, it) => (
        <Checkbox
          checked={itemDoneOf(it)}
          disabled={checklistLoading || checklistTask?.status === TASK_STATUS.COMPLETED}
          onChange={(e) => toggleChecklistItem(it, e.target.checked)}
        />
      ),
    },
    {
      title: "Item",
      key: "item",
      render: (_, it) => (
        <Text style={{ textDecoration: itemDoneOf(it) ? "line-through" : "none" }}>
          {itemDescOf(it) || `Item #${itemIdOf(it)}`}
        </Text>
      ),
    },
    {
      title: "Completed At",
      key: "completedAt",
      width: 220,
      render: (_, it) => {
        const done = itemDoneOf(it);
        const doneAt = itemDoneAtOf(it);

        if (!done) return <Text type="secondary">—</Text>;

        // If this is legacy data (completed but no timestamp), show a sensible label
        if (!doneAt) {
          return (
            <Tooltip title="No completion timestamp stored (older checklist items may not have CompletedAt).">
              <Text type="secondary">Completed</Text>
            </Tooltip>
          );
        }

        return <Text type="secondary">{new Date(doneAt).toLocaleString()}</Text>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, it) =>
        checklistTask?.status === TASK_STATUS.COMPLETED ? null : (
          <Popconfirm title="Delete this checklist item?" onConfirm={() => deleteChecklistItem(it)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
    },
  ].filter(Boolean);

  return (
    <div className="page-container">
      <Breadcrumb items={[{ title: "Dashboard", link: "/dashboard" }, { title: "Housekeeping" }]} />

      <Card
        style={{ marginBottom: 16 }}
        styles={{ body: { paddingBottom: 8, paddingTop: 12 } }}
        title={
          <Space>
            <HighlightOutlined />
            <Text strong>My Housekeeping Tasks</Text>
            <Badge count={tasks.length} color="blue" />
          </Space>
        }
        extra={
          <Space wrap>
            <Tooltip title="Auto refresh every 60s">
              <Space>
                <Text type="secondary">Auto</Text>
                <Switch checked={autoRefresh} onChange={setAutoRefresh} />
              </Space>
            </Tooltip>

            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
              Refresh
            </Button>

            <Tooltip title="How this page works">
              <Button type="text" icon={<InfoCircleOutlined />} onClick={() => setShowInfo(true)} />
            </Tooltip>
          </Space>
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={10}>
            <Search
              allowClear
              placeholder="Search by title, description, or room…"
              onSearch={setSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
            />
          </Col>

          <Col xs={24} md={8}>
            <Segmented
              block
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: "All", value: "All" },
                { label: "Pending", value: TASK_STATUS.PENDING },
                { label: "In Progress", value: TASK_STATUS.IN_PROGRESS },
                { label: "Completed", value: TASK_STATUS.COMPLETED },
              ]}
            />
          </Col>

          <Col xs={24} md={6}>
            <Select
              style={{ width: "100%" }}
              value={priorityFilter}
              onChange={setPriorityFilter}
              options={[
                { label: "All Priorities", value: "All" },
                { label: "Low", value: "Low" },
                { label: "Normal", value: "Normal" },
                { label: "High", value: "High" },
                { label: "Urgent", value: "Urgent" },
              ]}
            />
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic title={<Space><ClockCircleOutlined />Pending</Space>} value={counts.pending} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic title={<Space><SyncOutlined />In Progress</Space>} value={counts.inProgress} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic title={<Space><CheckCircleOutlined />Completed</Space>} value={counts.completed} />
            </Card>
          </Col>
        </Row>
      </Card>

      <Card>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Empty
            description={
              <>
                <p>{error}</p>
                <Button onClick={handleRefresh}>Retry</Button>
              </>
            }
          />
        ) : filteredTasks.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tasks match your filters" />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredTasks}
            rowKey="housekeepingTaskId"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            bordered
            size="middle"
          />
        )}
      </Card>

      {isModalOpen && (
        <HousekeepingModal
          open={isModalOpen}
          task={selectedTask}
          onClose={closeEditModal}
          onSave={handleTaskUpdate}
          statusOptions={Object.values(TASK_STATUS)}
          statusLabels={{
            [TASK_STATUS.PENDING]: STATUS_META[TASK_STATUS.PENDING].label,
            [TASK_STATUS.IN_PROGRESS]: STATUS_META[TASK_STATUS.IN_PROGRESS].label,
            [TASK_STATUS.COMPLETED]: STATUS_META[TASK_STATUS.COMPLETED].label,
          }}
        />
      )}

      <Modal
        title={
          <Space>
            <UnorderedListOutlined />
            <Text strong>
              Checklist{checklistTask?.housekeepingTaskId ? ` — Task #${checklistTask.housekeepingTaskId}` : ""}
            </Text>
          </Space>
        }
        open={checklistOpen}
        onCancel={() => setChecklistOpen(false)}
        footer={[
          <Button key="close" onClick={() => setChecklistOpen(false)}>
            Close
          </Button>,
          <Button
            key="complete"
            type="primary"
            icon={<CheckOutlined />}
            loading={checklistLoading}
            disabled={!checklistProgress.allDone || checklistTask?.status === TASK_STATUS.COMPLETED}
            onClick={markTaskCompletedFromChecklist}
          >
            Mark Task Completed
          </Button>,
        ]}
        width={900}
      >
        {!checklistTask ? (
          checklistLoading ? (
            <div style={{ padding: 36, textAlign: "center" }}>
              <Spin />
            </div>
          ) : (
            <Empty description="No task loaded" />
          )
        ) : (
          <Space orientation="vertical" style={{ width: "100%" }} size="middle">
            <Card size="small">
              <Row gutter={[12, 12]}>
                <Col xs={24} md={14}>
                  <Title level={5} style={{ margin: 0 }}>
                    {checklistTask.title}
                  </Title>
                  <Text type="secondary">Room {checklistTask.roomNumber || "N/A"}</Text>
                </Col>
                <Col xs={24} md={10} style={{ textAlign: "right" }}>
                  {renderStatus(checklistTask.status)}
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">
                      Progress: {checklistProgress.done}/{checklistProgress.total}
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>

            <div>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  value={newChecklistText}
                  onChange={(e) => setNewChecklistText(e.target.value)}
                  placeholder="Add a checklist item (e.g., Replace towels, Mop floor, Restock water)…"
                  onPressEnter={addChecklistItem}
                  disabled={checklistLoading || checklistTask.status === TASK_STATUS.COMPLETED}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addChecklistItem}
                  loading={checklistLoading}
                  disabled={!newChecklistText.trim() || checklistTask.status === TASK_STATUS.COMPLETED}
                >
                  Add
                </Button>
              </Space.Compact>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <Table
              columns={checklistColumns}
              dataSource={checklistTask.checklistItems || []}
              rowKey={(r) => String(itemIdOf(r) || r._tempId || `${Date.now()}-${Math.random()}`)}
              pagination={false}
              size="small"
              bordered
            />

            {!checklistProgress.allDone && checklistProgress.total > 0 && checklistTask.status !== TASK_STATUS.COMPLETED ? (
              <Tag color="orange" icon={<InfoCircleOutlined />}>
                Complete all items to enable “Mark Task Completed”.
              </Tag>
            ) : null}
          </Space>
        )}
      </Modal>

      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>How to use Housekeeping</Text>
          </Space>
        }
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>,
        ]}
        width={680}
      >
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Text>
            This page shows <strong>your assigned housekeeping tasks</strong>. Use filters to narrow by status or priority,
            and search to find rooms or titles quickly.
          </Text>
          <ul style={{ paddingInlineStart: 18, margin: 0 }}>
            <li>
              <Text>
                Click <strong>Edit</strong> to update notes and status.
              </Text>
            </li>
            <li>
              <Text>
                Click <strong>Checklist</strong> to manage checklist items. The task is automatically completed when all items are completed.
              </Text>
            </li>
            <li>
              <Text>
                Toggle <strong>Auto</strong> for automatic refresh every 60 seconds.
              </Text>
            </li>
          </ul>
        </Space>
      </Modal>
    </div>
  );
};

export default HousekeepingPage;
